import * as React from "react";
import { Button as ButtonPrimitive } from "@base-ui/react/button";

import { cx } from "../../cx";
import styles from "./button.module.css";

export type ButtonVariant =
  | "default"
  | "secondary"
  | "outline"
  | "ghost"
  | "link"
  | "destructive"
  | "book"
  | "chrome"
  | "chrome-danger"
  | "field";
export type ButtonTone = "default" | "danger" | "brand";
/** `round` is fully rounded: a pill on a text button, a circle on an icon size. */
export type ButtonShape = "default" | "round";
/** The `field` variant's value state: `placeholder` greys the text, `set`
 *  takes the brand border. Left out, the field shows neither. */
export type ButtonFieldState = "placeholder" | "set";
export type ButtonSize =
  | "sm"
  | "default"
  | "lg"
  | "icon-sm"
  | "icon"
  | "icon-lg";

export type ButtonVariantProps = {
  variant?: ButtonVariant;
  tone?: ButtonTone;
  size?: ButtonSize;
  pressed?: boolean;
};

/** Not in `ButtonVariantProps`, so a wrapper that spreads its rest props onto
 *  a link (traveler-web's `ButtonLink`) cannot leak them as DOM attributes. */
type ButtonShapeProps = {
  shape?: ButtonShape;
  fieldState?: ButtonFieldState;
};

interface ButtonProps
  extends
    Omit<ButtonPrimitive.Props, "className">,
    ButtonVariantProps,
    ButtonShapeProps {
  className?: string;
}

function rootAttributes({
  variant,
  tone,
  size,
  shape,
  fieldState,
  pressed,
}: Required<Omit<ButtonVariantProps, "pressed">> &
  Required<Pick<ButtonShapeProps, "shape">> &
  Pick<ButtonVariantProps, "pressed"> &
  Pick<ButtonShapeProps, "fieldState">) {
  return {
    "data-tui": "",
    "data-slot": "button",
    "data-variant": variant,
    "data-tone": tone === "default" ? undefined : tone,
    "data-size": size,
    "data-shape": shape === "default" ? undefined : shape,
    "data-field-state": fieldState,
    ...(pressed === undefined ? {} : { "aria-pressed": pressed }),
  };
}

function cloneTarget(
  target: React.ReactElement<Record<string, unknown>>,
  props: Record<string, unknown>,
  className: string,
  children: React.ReactNode,
) {
  return React.cloneElement(target, {
    ...props,
    ...target.props,
    className: cx(className, target.props.className as string | undefined),
    ...(children === undefined ? {} : { children }),
  });
}

function Button({
  className,
  variant = "default",
  tone = "default",
  size = "default",
  shape = "default",
  fieldState,
  pressed,
  render,
  children,
  type,
  ...props
}: ButtonProps) {
  const rootProps = rootAttributes({
    variant,
    tone,
    size,
    shape,
    fieldState,
    pressed,
  });
  const rootClass = cx(styles.button, className);

  // A non-<button> render target (e.g. a link) keeps its own semantics: Base UI
  // would add role="button" to it, so it is cloned with our props instead.
  if (React.isValidElement(render) && render.type !== "button") {
    return cloneTarget(
      render as React.ReactElement<Record<string, unknown>>,
      { ...rootProps, ...props },
      rootClass,
      children,
    );
  }

  return (
    <ButtonPrimitive
      {...rootProps}
      type={render ? undefined : (type ?? "button")}
      className={rootClass}
      {...(render ? { render } : {})}
      {...props}
    >
      {children}
    </ButtonPrimitive>
  );
}

export { Button };
